import type { AxiosError } from 'axios';
import type { UseQueryResult } from 'react-query';
import { useQuery } from 'react-query';

import * as insightApi from '@hotjar/webapp/domains/highlights/recording/api';
import { RECORDING_HIGHLIGHT_STATUS } from '@hotjar/webapp/domains/highlights/api';
import { useRef } from 'react';

type ApiFunction = typeof insightApi.getStatus;
type ApiResponseType = Awaited<ReturnType<ApiFunction>>;

export function useRefreshedRecordingHighlight<
  TContentStatus extends 'creating' | unknown,
  THighlight extends {
    id: string;
    contentStatus: TContentStatus;
    metadata?: { videoUrl: string | null };
    thumbnailUrl?: string | null;
  },
>(
  siteId: string | null,
  initialHighlight: THighlight,
): {
  data: THighlight;
  status: UseQueryResult<THighlight, AxiosError>['status'];
} {
  const latestContentStatus = useRef(initialHighlight.contentStatus);
  const queryResult = useQuery<ApiResponseType, AxiosError>(
    [RECORDING_HIGHLIGHT_STATUS, siteId, initialHighlight.id],
    () => insightApi.getStatus(siteId as string, initialHighlight.id),
    {
      enabled: siteId !== null && latestContentStatus.current === 'creating',
      staleTime: 5 * 60 * 1000, // 5 minutes.
      refetchInterval: 5_000, // 5 seconds.
    },
  );

  if (queryResult.data) {
    latestContentStatus.current = queryResult.data.contentStatus as TContentStatus;
  }

  return {
    ...queryResult,
    data: {
      ...initialHighlight,
      contentStatus: latestContentStatus.current,
      metadata: {
        ...initialHighlight.metadata,
        videoUrl: queryResult.data?.videoUrl ?? initialHighlight.metadata?.videoUrl,
      },
      thumbnailUrl: queryResult.data?.thumbnailUrl ?? initialHighlight.thumbnailUrl,
    },
  };
}
